const API_URL = 'https://app.solvere.pro/api';

function getToken() {
  return localStorage.getItem('fintech_token');
}

function setToken(token) {
  localStorage.setItem('fintech_token', token);
}

function removeToken() {
  localStorage.removeItem('fintech_token');
}

async function request(endpoint, options = {}) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
  const data = await res.json();
  if (!res.ok) throw { status: res.status, ...data };
  return data;
}

export const api = {
  login: (cpf, password, totpCode, deviceCode) => request('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ cpf, password, totpCode: totpCode || undefined, deviceCode: deviceCode || undefined }),
  }),
  logout: () => request('/auth/logout', { method: 'POST' }),
  logoutAll: () => request('/auth/logout-all', { method: 'POST' }),
  me: () => request('/auth/me'),
  getSessions: () => request('/auth/sessions'),

  lookupCpf: (cpf) => request(`/admin/cpf-lookup/${cpf}`),
  getUsers: () => request('/admin/users'),
  createUser: (data) => request('/admin/users', { method: 'POST', body: JSON.stringify(data) }),
  updateUser: (id, data) => request(`/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  updateUserStatus: (id, status) => request(`/admin/users/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  getStats: () => request('/admin/stats'),
  getImportModel: () => request('/admin/import-model'),
  importUserWithTransactions: (data) => request('/admin/import', { method: 'POST', body: JSON.stringify(data) }),
  importTransactions: (userId, data) => request(`/admin/users/${userId}/import-transactions`, { method: 'POST', body: JSON.stringify(data) }),

  // Password reset
  requestPasswordReset: (cpf) => request('/auth/reset-password/request', { method: 'POST', body: JSON.stringify({ cpf }) }),
  verifyPasswordReset: (cpf, code, newPassword) => request('/auth/reset-password/verify', { method: 'POST', body: JSON.stringify({ cpf, code, newPassword }) }),

  // Devices
  getDevices: () => request('/auth/devices'),
  removeDevice: (id) => request(`/auth/devices/${id}`, { method: 'DELETE' }),

  updateProfile: (data) => request('/auth/profile', { method: 'PUT', body: JSON.stringify(data) }),
  changePassword: (data) => request('/auth/password', { method: 'PUT', body: JSON.stringify(data) }),

  // 2FA
  setup2FA: () => request('/auth/2fa/setup', { method: 'POST' }),
  verify2FA: (code) => request('/auth/2fa/verify', { method: 'POST', body: JSON.stringify({ code }) }),
  disable2FA: (password) => request('/auth/2fa/disable', { method: 'POST', body: JSON.stringify({ password }) }),

  getTransactions: (userId) => request(`/transactions${userId ? `?user_id=${userId}` : ''}`),
  createTransaction: (data) => request('/transactions', { method: 'POST', body: JSON.stringify(data) }),

  // PIX
  pixDeposit: (data) => request('/transactions/pix/deposit', { method: 'POST', body: JSON.stringify(data) }),
  pixWithdraw: (data) => request('/transactions/pix/withdraw', { method: 'POST', body: JSON.stringify(data) }),
  pixStatus: (id) => request(`/transactions/pix/status/${id}`),
  pixOperations: () => request('/transactions/pix/operations'),

  // Deposit schedules
  getMySchedules: () => request('/auth/schedules'),
  getUserSchedules: (userId) => request(`/admin/users/${userId}/schedules`),
  createSchedule: (userId, data) => request(`/admin/users/${userId}/schedules`, { method: 'POST', body: JSON.stringify(data) }),
  updateSchedule: (id, data) => request(`/admin/schedules/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteSchedule: (id) => request(`/admin/schedules/${id}`, { method: 'DELETE' }),

  // Loans
  getMyLoans: () => request('/auth/loans'),
  getUserLoans: (userId) => request(`/admin/users/${userId}/loans`),
  getAllLoans: () => request('/admin/loans'),
  createLoan: (userId, data) => request(`/admin/users/${userId}/loans`, { method: 'POST', body: JSON.stringify(data) }),
  updateLoan: (id, data) => request(`/admin/loans/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteLoan: (id) => request(`/admin/loans/${id}`, { method: 'DELETE' }),
  updateInstallment: (id, data) => request(`/admin/installments/${id}`, { method: 'PUT', body: JSON.stringify(data) }),

  // Webhooks
  getWebhooks: () => request('/admin/webhooks'),
  createWebhook: (data) => request('/admin/webhooks', { method: 'POST', body: JSON.stringify(data) }),
  updateWebhook: (id, data) => request(`/admin/webhooks/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteWebhook: (id) => request(`/admin/webhooks/${id}`, { method: 'DELETE' }),
  testWebhook: (id) => request(`/admin/webhooks/${id}/test`, { method: 'POST' }),

  // Email templates
  getEmailTemplates: () => request('/admin/email-templates'),
  createEmailTemplate: (data) => request('/admin/email-templates', { method: 'POST', body: JSON.stringify(data) }),
  updateEmailTemplate: (id, data) => request(`/admin/email-templates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteEmailTemplate: (id) => request(`/admin/email-templates/${id}`, { method: 'DELETE' }),

  // Email send
  sendEmail: (data) => request('/admin/email/send', { method: 'POST', body: JSON.stringify(data) }),
  sendBulkEmail: (data) => request('/admin/email/send-bulk', { method: 'POST', body: JSON.stringify(data) }),
  getNoDepositUsers: () => request('/admin/email/no-deposit-users'),
  sendReminders: (data) => request('/admin/email/send-reminders', { method: 'POST', body: JSON.stringify(data || {}) }),

  // Email logs & stats
  getEmailLogs: (params) => request(`/admin/email-logs${params ? '?' + new URLSearchParams(params) : ''}`),
  getUserEmailLogs: (userId) => request(`/admin/users/${userId}/email-logs`),
  getEmailStats: () => request('/admin/email/stats'),
  getEmailClicks: (logId) => request(`/admin/email-logs/${logId}/clicks`),

  // Activity Logs
  getActivityLogs: (params) => request(`/admin/activity-logs${params ? '?' + new URLSearchParams(params) : ''}`),
  getActivitySummary: (date) => request(`/admin/activity-logs/summary${date ? '?date=' + date : ''}`),

  // Maintenance
  checkMaintenance: () => request('/auth/maintenance'),
  getMaintenanceStatus: () => request('/admin/maintenance'),
  toggleMaintenance: (enabled) => request('/admin/maintenance', { method: 'PUT', body: JSON.stringify({ enabled }) }),

  // Automation Engine
  getAutomationStatus: () => request('/admin/automation/status'),
  toggleAutomation: (enabled) => request('/admin/automation/toggle', { method: 'PUT', body: JSON.stringify({ enabled }) }),
  runAutomation: (type) => request('/admin/automation/run', { method: 'POST', body: JSON.stringify({ type }) }),
  getHealthScores: () => request('/admin/automation/health-scores'),
  getUserHealthScore: (userId) => request(`/admin/automation/health-scores/${userId}`),
  getAutomationLogs: (params) => request(`/admin/automation/logs${params ? '?' + new URLSearchParams(params) : ''}`),
  getAutomationPreview: () => request('/admin/automation/preview'),
  getAutomationConversions: () => request('/admin/automation/conversions'),
  getAutomationJourneys: () => request('/admin/automation/journeys'),
  getAutomationRuns: () => request('/admin/automation/runs'),
};

export { getToken, setToken, removeToken };
